import { useEffect, useState } from 'react';
import api from '../api/axios';
import PageTitle from '../components/PageTitle';

const STATUS_COLORS = {
  NEW:       { bg: 'var(--bluel)',  color: 'var(--blue)',  label: 'New' },
  CONTACTED: { bg: '#FFF3E0',       color: '#E65100',      label: 'Contacted' },
  CONVERTED: { bg: 'var(--greenl)', color: 'var(--green)', label: 'Converted' },
  CLOSED:    { bg: 'var(--creamd)', color: 'var(--muted)', label: 'Closed' },
};

export default function AdminEnquiries() {
  const [enquiries, setEnquiries] = useState([]);
  const [filtered,  setFiltered]  = useState([]);
  const [filter,    setFilter]    = useState('ALL');
  const [search,    setSearch]    = useState('');
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState(null);
  const [note,      setNote]      = useState('');
  const [saving,    setSaving]    = useState(false);
  const [msg,       setMsg]       = useState(null);

  const load = () => api.get('/api/enquiry/all')
    .then(r => { setEnquiries(r.data); setFiltered(r.data); })
    .catch(console.error).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  useEffect(() => {
    let list = enquiries;
    if (filter !== 'ALL') list = list.filter(e => e.status === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(e => e.fullName?.toLowerCase().includes(q) || e.phone?.includes(q) || e.service?.toLowerCase().includes(q));
    }
    setFiltered(list);
  }, [filter, search, enquiries]);

  const updateStatus = async (id, status) => {
    setSaving(true);
    try {
      const params = new URLSearchParams({ status });
      if (note) params.append('note', note);
      const { data } = await api.patch(`/api/enquiry/${id}/status?${params}`);
      setEnquiries(prev => prev.map(e => e.id === id ? data : e));
      setSelected(data);
      setMsg({ type: 'success', text: 'Status updated' });
    } catch (err) {
      setMsg({ type: 'error', text: 'Update failed' });
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 3000);
    }
  };

  const newCount = enquiries.filter(e => e.status === 'NEW').length;

  if (loading) return <div className="centered"><span className="spinner spinner-dark" /></div>;

  return (
    <>
      <PageTitle title="Enquiries" />
      <div className="page-hero">
        <div className="page-hero-label">Admin Panel</div>
        <h1>Callback Enquiries</h1>
        <p>{enquiries.length} total enquiries {newCount > 0 && `· ${newCount} new`}</p>
      </div>

      <div className="page-wide">
        {/* Stats */}
        <div className="stat-grid" style={{ marginBottom: '1.5rem' }}>
          {[
            { label: 'Total',     value: enquiries.length,                                              icon: '📋' },
            { label: 'New',       value: enquiries.filter(e => e.status === 'NEW').length,       icon: '🔔', highlight: true },
            { label: 'Contacted', value: enquiries.filter(e => e.status === 'CONTACTED').length, icon: '📞' },
            { label: 'Converted', value: enquiries.filter(e => e.status === 'CONVERTED').length, icon: '✅' },
          ].map(s => (
            <div key={s.label} className="stat-card"
              style={s.highlight && s.value > 0 ? { border: '2px solid var(--gold)' } : {}}>
              <div style={{ fontSize: '1.3rem', marginBottom: '.2rem' }}>{s.icon}</div>
              <div className="stat-num">{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 380px' : '1fr', gap: '1.5rem' }}>
          {/* List */}
          <div>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <input placeholder="Search by name, phone, service..." value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ flex: 1, minWidth: 200, padding: '.6rem 1rem', border: '1.5px solid var(--border)', borderRadius: 10, fontFamily: 'DM Sans, sans-serif', fontSize: '.88rem', background: '#fff', outline: 'none' }} />
              <select value={filter} onChange={e => setFilter(e.target.value)}
                style={{ padding: '.6rem 1rem', border: '1.5px solid var(--border)', borderRadius: 10, fontFamily: 'DM Sans, sans-serif', fontSize: '.88rem', background: '#fff' }}>
                <option value="ALL">All Statuses</option>
                <option value="NEW">New</option>
                <option value="CONTACTED">Contacted</option>
                <option value="CONVERTED">Converted</option>
                <option value="CLOSED">Closed</option>
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
              {filtered.length === 0 && (
                <div className="card" style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--muted)' }}>
                  No enquiries found
                </div>
              )}
              {filtered.map(e => {
                const sc = STATUS_COLORS[e.status] || STATUS_COLORS.NEW;
                const isSelected = selected?.id === e.id;
                return (
                  <div key={e.id}
                    onClick={() => { setSelected(e); setNote(e.adminNote || ''); }}
                    style={{ background: '#fff', border: `1.5px solid ${isSelected ? 'var(--navy)' : 'var(--border)'}`, borderRadius: 12, padding: '1.1rem 1.25rem', cursor: 'pointer', transition: 'all .2s' }}
                    onMouseOver={ev => { if (!isSelected) ev.currentTarget.style.borderColor = 'var(--gold)'; }}
                    onMouseOut={ev => { if (!isSelected) ev.currentTarget.style.borderColor = 'var(--border)'; }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '.95rem', color: 'var(--navy)', marginBottom: '.2rem' }}>
                          {e.fullName}
                          {e.status === 'NEW' && <span style={{ background: 'var(--gold)', color: 'var(--navy)', fontSize: '.65rem', fontWeight: 700, padding: '.1rem .45rem', borderRadius: 20, marginLeft: '.5rem' }}>NEW</span>}
                        </div>
                        <div style={{ fontSize: '.82rem', color: 'var(--muted)' }}>📞 {e.phone} {e.email ? `· ✉️ ${e.email}` : ''}</div>
                      </div>
                      <span style={{ background: sc.bg, color: sc.color, fontSize: '.72rem', fontWeight: 600, padding: '.2rem .6rem', borderRadius: 20 }}>{sc.label}</span>
                    </div>
                    {e.service && (
                      <div style={{ marginTop: '.5rem', display: 'inline-block', background: 'var(--creamd)', color: 'var(--muted)', fontSize: '.75rem', padding: '.15rem .5rem', borderRadius: 6 }}>
                        {e.service}
                      </div>
                    )}
                    {e.message && (
                      <div style={{ marginTop: '.5rem', fontSize: '.82rem', color: 'var(--muted)', fontStyle: 'italic' }}>
                        "{e.message.length > 80 ? e.message.slice(0, 80) + '...' : e.message}"
                      </div>
                    )}
                    <div style={{ fontSize: '.72rem', color: 'var(--muted)', marginTop: '.4rem' }}>
                      {new Date(e.createdAt).toLocaleString('en-IN')}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Detail panel */}
          {selected && (
            <div>
              <div className="card" style={{ position: 'sticky', top: '5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                  <h3 style={{ fontSize: '1rem', color: 'var(--navy)' }}>Enquiry #{selected.id}</h3>
                  <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: '1.1rem' }}>✕</button>
                </div>

                {msg && <div className={`alert alert-${msg.type}`} style={{ fontSize: '.82rem', marginBottom: '1rem' }}>{msg.text}</div>}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem', marginBottom: '1.25rem' }}>
                  {[['Name', selected.fullName], ['Phone', selected.phone], ['Email', selected.email], ['Service', selected.service], ['Message', selected.message]].map(([l, v]) => v ? (
                    <div key={l} style={{ display: 'flex', gap: '.75rem', fontSize: '.85rem', padding: '.35rem 0', borderBottom: '1px solid var(--creamd)' }}>
                      <span style={{ color: 'var(--muted)', minWidth: 65 }}>{l}</span>
                      <span style={{ color: 'var(--navy)', fontWeight: 500 }}>{v}</span>
                    </div>
                  ) : null)}
                </div>

                <div className="field" style={{ marginBottom: '1rem' }}>
                  <label>Update Status</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.5rem', marginTop: '.35rem' }}>
                    {['CONTACTED', 'CONVERTED', 'CLOSED', 'NEW'].map(s => {
                      const sc = STATUS_COLORS[s];
                      return (
                        <button key={s} onClick={() => updateStatus(selected.id, s)} disabled={saving}
                          style={{ background: selected.status === s ? sc.color : sc.bg, color: selected.status === s ? '#fff' : sc.color, border: `1px solid ${sc.color}`, borderRadius: 8, padding: '.45rem', fontFamily: 'DM Sans, sans-serif', fontSize: '.78rem', fontWeight: 600, cursor: 'pointer' }}>
                          {sc.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="field" style={{ marginBottom: '1rem' }}>
                  <label>Note</label>
                  <textarea rows={3} value={note} onChange={e => setNote(e.target.value)}
                    placeholder="Add a note about this enquiry..." />
                </div>

                <button className="btn btn-primary btn-sm" style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => updateStatus(selected.id, selected.status)} disabled={saving}>
                  {saving ? 'Saving...' : '💾 Save Note'}
                </button>

                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--creamd)', fontSize: '.73rem', color: 'var(--muted)' }}>
                  Received: {new Date(selected.createdAt).toLocaleString('en-IN')}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
